import { useEffect, useMemo, useRef } from "react";
import { BrowserRouter } from "react-router-dom";
import { ErrorBoundary } from "./context/ErrorBoundary";
import { DeviceProvider } from "./context/DeviceProvider";
import { UnitsProvider } from "./context/UnitsProvider";
import { AuthProvider, useAuth } from "./context/AuthProvider";
import { ToastProvider, useToast } from "./context/ToastProvider";
import { PlanGenerationProvider } from "./context/PlanGenerationProvider";
import { ModalProvider } from "./context/ModalProvider";
import { AppRoutes } from "./router";
import { ReconcileRetryBanner } from "./components/FG/ReconcileRetryBanner";
import { PromotedSessionBanner } from "./components/FG/PromotedSessionBanner";
import { SignedOutBanner } from "./components/FG/SignedOutBanner";
import { PendingToast } from "./components/FG/PendingToast";
import { PreviewGateScreen } from "./screens/PreviewGate";
import { Spinner } from "./components/ui";
import { useEventMutations } from "./hooks/useEvents";
import { getAnonPendingRaceEvents } from "./lib/anonDraft";
import { replayAnonPendingRaceEvents } from "./lib/anonRaceReplay";
import { linkTrainingPlanEvent } from "./lib/trainingPlanApi";
import { requestPlannerRefresh } from "./hooks/usePlannedWorkouts";
import type { BootPayload } from "./lib/boot";

/**
 * Root of the customer SPA. The provider stack — outermost to innermost — is:
 *   ErrorBoundary  → catches render-time errors and reports to /api/telemetry.
 *   BrowserRouter  → hash-free client-side routing.
 *   DeviceProvider → responsive bucket from `matchMedia`; consumed by FG components.
 *   AuthProvider   → loads boot data + refreshes via /api/customer/context.
 *   UnitsProvider  → miles/km display preference; local cache + per-customer
 *                    server sync (sits inside Auth so it can read the session).
 *   ToastProvider  → portal-mounted toasts.
 *   PlanGenerationProvider → app-wide fuel-plan build watcher: outcome toasts
 *                    on any page + cross-tab bus publish (needs Toast+Router+Auth).
 *   ModalProvider  → modal stack (scrim + portal), shared by Customize / Add Workout.
 *   <Gate>         → renders the loading interstitial until `/api/customer/context`
 *                    resolves, then hands off to <AppRoutes>.
 *
 * `boot` is the payload the EJS shell serialised into `window.__FG_BOOT__`. We
 * accept it as a prop here so `main.tsx` can read the global once and pass it
 * down — the provider hooks themselves are global-free for testing.
 */
function readProxyBasename(): string {
  const el = document.getElementById("customer-app-root");
  const base = (el?.getAttribute("data-app-proxy-base") ?? "").replace(/\/+$/, "");
  return base || "/";
}

export function App({ boot: _boot }: { boot: BootPayload }) {
  const basename = useMemo(readProxyBasename, []);
  return (
    <ErrorBoundary>
      <BrowserRouter
        basename={basename}
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <DeviceProvider>
          <AuthProvider>
            <UnitsProvider>
              <ToastProvider>
                <PlanGenerationProvider>
                  <ModalProvider>
                    <MergeToast />
                    <PendingToast />
                    <ReconcileRetryBanner />
                    {/* E1: promoted-hint and signed-out are mutually exclusive
                        in AuthProvider (promoted-hint wins), so these two never
                        stack — at most one renders. */}
                    <PromotedSessionBanner />
                    <SignedOutBanner />
                    <PromotionToast />
                    <AnonRaceReplay />
                    <Gate />
                  </ModalProvider>
                </PlanGenerationProvider>
              </ToastProvider>
            </UnitsProvider>
          </AuthProvider>
        </DeviceProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

/** Interstitial while AuthProvider fires its initial /api/customer/context ping. */
function Gate() {
  const { state } = useAuth();
  if (state.kind === "loading") {
    return (
      <div className="fg fg-gate">
        <Spinner accessibilityLabel="Loading your session" className="fg-gate__spinner" />
      </div>
    );
  }
  if (state.kind === "preview_gate") {
    return <PreviewGateScreen previewGate={state.previewGate} />;
  }
  if (state.kind === "error") {
    return (
      <div className="fg fg-gate">
        <div className="fg-gate__inner">
          <span className="fg-eyebrow">Session failed</span>
          <h1 className="fg-h3">Couldn't load your session</h1>
          <p className="fg-body">
            {state.error ?? "Something went wrong loading your session. Try again in a moment."}
          </p>
          <button
            type="button"
            className="fg-btn fg-btn--primary"
            onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }
  return <AppRoutes />;
}

/**
 * Watches for a pending anon→customer merge summary and fires the
 * "Plan saved to your account" toast once. Lives inside ToastProvider so it
 * can push; reads the one-shot summary from AuthProvider.
 */
// Exported for unit tests (App.mergeToast.test.tsx). Mounted once in <App>.
export function MergeToast() {
  const { state, consumeMergeSummary } = useAuth();
  const { push } = useToast();
  useEffect(() => {
    if (state.kind !== "customer") return;
    const summary = consumeMergeSummary();
    if (!summary) return;
    // C2: anon can now OWN a journey/event, so a merge that moved ONLY a race
    // and its plan (no synced-down workouts) is still real — count journeys and
    // events toward "something moved". `?? 0` for pre-C1 servers that omit them.
    const fuelJourneys = summary.fuelJourneys ?? 0;
    const customerEvents = summary.customerEvents ?? 0;
    const moved = summary.plannedWorkouts + summary.fuelPlans + fuelJourneys + customerEvents;
    // Nothing moved and no training-data link → no toast (the old `moved === 0`
    // suppression, now widened to include journeys/events).
    if (moved === 0 && !summary.connectionMerged) return;
    // Detail line, most-specific first: workouts → generic plan → race/plan
    // (journey/event-only merge, the case the old copy silently dropped) →
    // connection-only.
    const detail =
      summary.plannedWorkouts > 0
        ? `${summary.plannedWorkouts} workout${summary.plannedWorkouts === 1 ? "" : "s"} were added to your account.`
        : summary.fuelPlans > 0
          ? "Your fueling plan is now on your account."
          : fuelJourneys > 0 || customerEvents > 0
            ? "Your race and plan are saved to your account."
            : "Your training connection is now on your account.";
    push({
      tone: "success",
      message: "Plan saved to your account",
      detail
    });
  }, [state, consumeMergeSummary, push]);
  return null;
}

/**
 * Surfaces the login draft-promotion result once (Slice A — guest preferences /
 * physiology drafts saved onto the account during the auth bootstrap). Mirrors
 * `<MergeToast>`: reads the one-shot summary parked on AuthProvider.
 */
function PromotionToast() {
  const { state, consumePromotionSummary } = useAuth();
  const { push } = useToast();
  useEffect(() => {
    if (state.kind !== "customer") return;
    const summary = consumePromotionSummary();
    if (!summary) return;
    push({
      tone: "success",
      message: summary.preferences
        ? "Preferences saved to your account"
        : "Profile saved to your account",
      detail:
        summary.preferences && summary.physiology
          ? "Your fueling preferences and athlete details from this device are now on your profile."
          : summary.preferences
            ? "Your fueling preferences from this device are now on your profile."
            : "Your athlete details from this device are now on your profile."
    });
  }, [state, consumePromotionSummary, push]);
  return null;
}

/**
 * Replays a guest's held race drafts ({@link getAnonPendingRaceEvents}) once
 * they sign in. Anon sessions own real `CustomerEvent`s now
 * (customer-event-anon-ownable), so most guest races arrive through the server
 * merge with their workouts — nothing to replay. This covers what is left:
 * drafts from the paths that still reach no server row (a tune-up/milestone
 * setup, the "just save the event" exit) and drafts written by an older build
 * still sitting on a returning visitor's device.
 *
 * On the anon→customer transition we replay EVERY held draft in setup order via
 * `replayAnonPendingRaceEvents` — create through the standard events API
 * (CSRF-guarded + server-deduped, so a race the merge already brought over
 * dedupe-hits instead of duplicating), then link onto its plan group / merged
 * build journey so the calendar banner names the race. Each entry is cleared
 * from storage as it succeeds; a failure keeps the failed entry + remainder so
 * the next login retries the tail (creating and linking are both idempotent).
 */
function AnonRaceReplay() {
  const { state, refreshCsrf } = useAuth();
  const { create } = useEventMutations();
  const csrfToken = state.kind === "customer" ? state.csrfToken : null;
  const doneRef = useRef(false);
  useEffect(() => {
    if (state.kind !== "customer" || !csrfToken || doneRef.current) return;
    if (getAnonPendingRaceEvents().length === 0) return;
    doneRef.current = true; // guard against re-fire within this mount
    void replayAnonPendingRaceEvents({
      createEvent: create,
      linkEvent: (target) => linkTrainingPlanEvent(target, csrfToken, refreshCsrf)
    }).then(({ linked, failed }) => {
      // The links mutated workout rows outside React — nudge any mounted
      // planner to refetch so the race names appear without a manual reload.
      if (linked > 0) requestPlannerRefresh();
      // Something errored — the failed entry + remainder are still in
      // storage; re-arm so the next mount retries them.
      if (failed) doneRef.current = false;
    });
  }, [state, csrfToken, create, refreshCsrf]);
  return null;
}
